/**
 * Cache keys, kept in one place so invalidation cannot drift.
 *
 * They live apart from the hooks because the provider that owns the cache also
 * needs them — to drop everything when a session ends, and to re-read `/me`
 * when the server says the account may not act.
 */

/** Prefix every dataset-shaped key sits under, so one act can invalidate them all. */
export const DATASETS_KEY = ["datasets"] as const;

export const queryKeys = {
  authConfig: ["auth", "config"] as const,
  // Published bounds, lists and defaults. Its own root and not under `me`: a
  // profile that is re-read because the console was paused says nothing about
  // the metric table, and a prefix match would fetch it again for nothing.
  config: ["config"] as const,
  me: ["me"] as const,
  // Deliberately not under `me` either: the profile is re-read on events that
  // say nothing about credentials, and a prefix match would turn each of those
  // into a second request for a list only the Account page ever shows.
  consoleKeys: ["console-keys"] as const,
  accounts: ["accounts"] as const,
  account: (id: string) => ["accounts", id] as const,
  // The account filter is part of the key: a filtered catalogue is its own
  // keyset walk, and two filters share no cursor.
  datasets: (account: string | undefined) =>
    ["datasets", "list", account ?? "all"] as const,
  dataset: (id: string) => ["datasets", id] as const,
  // Under the dataset's own prefix, every one of them: changing a consent, a
  // bar or a version changes what the dataset page says about itself, and one
  // invalidation should reach the whole screen rather than five.
  datasetVersions: (id: string) => ["datasets", id, "versions"] as const,
  datasetVersion: (id: string, number: number) =>
    ["datasets", id, "versions", number] as const,
  // The purpose is part of the key: the server writes a different block for a
  // different purpose, and two purposes are two documents.
  fetchLines: (id: string, number: number, purpose: string) =>
    ["datasets", id, "versions", number, "fetch-lines", purpose] as const,
  datasetConsent: (id: string) => ["datasets", id, "consent"] as const,
  datasetTargets: (id: string) => ["datasets", id, "target"] as const,
  datasetEvaluations: (id: string) => ["datasets", id, "evaluations"] as const,
  datasetReads: (id: string) => ["datasets", id, "reads"] as const,
  datasetDeletion: (id: string) => ["datasets", id, "deletion"] as const,
  adminUsers: ["admin", "users"] as const,
  adminAudit: (limit: number) => ["admin", "audit", limit] as const,
  adminStorage: ["admin", "storage"] as const,
};
